import { Group, Paper, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { formatMoney } from '../../lib/format';

interface Receivable {
  channelId?: string;
  grossMinor?: number;
  currency?: string;
}

/**
 * How much each platform is sitting on, above the line-by-line ledger.
 *
 * The question this tab gets opened with is "how much is the delivery platform holding of mine?",
 * and the answer was five hundred rows of individual orders that the reader had to add up. The
 * ledger still matters when a statement has to be checked order by order — but not before the
 * total it adds up to.
 *
 * Totalled per currency as well as per platform: a platform that pays some orders in dollars and
 * some in lempiras has two separate debts, and one added figure would be a number that exists
 * nowhere in the real world.
 */
export function OwedByPlatform({ path }: { path: string }) {
  const channels = useEntityIndex('channel');
  // Same key and request as the table underneath, so this shares its fetch rather than doubling it.
  const query = useQuery({
    queryKey: listKey(path),
    queryFn: () => rawApi<Receivable[]>('GET', path),
  });

  const totals = new Map<string, { channelId: string; currency: string; minor: number }>();
  for (const row of query.data ?? []) {
    const channelId = row.channelId ?? '';
    const currency = row.currency ?? 'HNL';
    const key = `${channelId}|${currency}`;
    const found = totals.get(key) ?? { channelId, currency, minor: 0 };
    found.minor += typeof row.grossMinor === 'number' ? row.grossMinor : 0;
    totals.set(key, found);
  }
  const rows = [...totals.values()].sort((a, b) => b.minor - a.minor);
  if (rows.length === 0) return null;

  return (
    <Group gap="sm" mb="md" align="stretch">
      {rows.map((total) => (
        <Paper key={`${total.channelId}|${total.currency}`} withBorder p="sm" radius="md" miw={180}>
          <Text size="xs" c="dimmed">
            {channels.label(total.channelId)}
          </Text>
          <Text size="xl" fw={600}>
            {formatMoney(total.minor, total.currency)}
          </Text>
        </Paper>
      ))}
    </Group>
  );
}
